import { createSelector } from 'reselect';
import { convertISODateToString } from '@beef/shop-common';

import { remainsNumToText } from 'pushEvent/helpers';
import { removeArrayDuplicates } from 'utils/remove-duplicates';
import { blankArr, blankObj, convertListToObjectBy } from 'utils/fn';
import { composeContextSelectorGroup, createReduceSelector } from 'utils/redux';
import { createMarkupReduceSelector, createAdjustableMarkupReduceSelector } from 'utils/markup';
import { getBasketProductIds } from 'store/common/basket/selectors';
import { getComparedProductIds } from 'store/common/compare/selectors';
import { getCurrentCategoriesPath } from 'store/common/navigation/selectors';
import * as Product from 'store/common/constants/product';
import { cookiesDataService } from 'services/cookiesDataService';
import { COOKIES_NAMES } from 'constants/cookies';
import { selectDisplayShortName } from 'store/common/settings/selectors';

import {
  getCatalogData,
  getCatalogSubCollectionId,
  getCatalogSubCollectionName,
} from '../selectors';
import { CATALOG_UPDATE_ERROR_EMPTY } from '../constants';
import { REQUEST_INPROGRESS } from '../../constants';
import { CatalogProductsContext } from './context';

const getCatalogProductsData = (state) => getCatalogData(state).products || blankObj;

export const getCatalogProductsResponseData = (state) =>
  getCatalogProductsData(state).updateResponseData;

export const selectCatalogShowMoreLoading = (state) =>
  getCatalogProductsData(state).showMoreLoading;
export const getCataloMainCollectionIdSelectors = (state) =>
  getCatalogProductsData(state).mainCollectionId;
export const getCataloMainCollectionNameSelectors = (state) =>
  getCatalogProductsData(state)?.mainCollectionName;
export const getCatalogProductsRecentlyViewedProducts = (state) => state.recentlyViewedProducts;
export const getCatalogProductsListRaw = (state) => getCatalogProductsData(state).list || blankArr;

export const getCatalogProductsNotEmpty = (state) => !!getCatalogProductsListRaw(state).length;

export const getCatalogProductsUpdateMethodUrl = (state) =>
  getCatalogProductsData(state).updateMethodUrl;
export const getCatalogProductsRequestStatus = (state) =>
  getCatalogProductsData(state).updateRequestStatus;
export const getCatalogProductsReadyToSwitch = (state) =>
  getCatalogProductsData(state).updateReadyToSwitch;
export const getCatalogProductsErrorMessageType = (state) =>
  getCatalogProductsData(state).updateRequestErrorType || '';

export const getCatalogProductsErrorMessage = createSelector(
  getCatalogProductsErrorMessageType,
  getCatalogProductsData,
  getCatalogProductsNotEmpty,
  (type, data, isNotEmptyCatalog) => {
    if (type === CATALOG_UPDATE_ERROR_EMPTY || !isNotEmptyCatalog) {
      return data.requestEmptyListErrorMessage;
    }
    return data.requestUnexpectedErrorMessage;
  },
);

/**
 * Product paging data
 */
export const getCatalogPaginationData = (state) => getCatalogProductsData(state).paging || blankObj;
export const getCatalogCurrentPage = (state) => getCatalogPaginationData(state).currentPage;
export const getCatalogPerPage = (state) => getCatalogPaginationData(state).perPage;
export const getCatalogTotalCount = (state) => getCatalogPaginationData(state).totalCount || 0;
export const getCatalogTotalPages = createSelector(
  getCatalogTotalCount,
  getCatalogPerPage,
  (totalCount, perPage) => parseInt((totalCount + perPage - 1) / perPage, 10),
);
export const getCatalogPaginationAvailability = (state) => getCatalogTotalPages(state) > 1;
export const getCatalogCurrentPageSkipComposer = createSelector(
  getCatalogPerPage,
  (perPage) => (page) => perPage * (page - 1),
);

export const selectIsShowMoreVisible = createSelector(
  getCatalogCurrentPage,
  getCatalogTotalPages,
  (current, totalPages) => current !== totalPages,
);

/**
 * Selectors for list of products
 */
export const getCatalogProductsList = createSelector(
  getCatalogProductsListRaw,
  getBasketProductIds,
  selectDisplayShortName,
  (list, addedItems, displayShortName) => {
    const favoriteList = cookiesDataService.getDataList(COOKIES_NAMES.FAVORITE_DEVICES);
    const favoriteListSet = new Set(favoriteList);

    return list.map(({ id, ...fields }) => ({
      ...fields,
      id,
      isInBasket: addedItems.indexOf(id) !== -1,
      isFavorite: favoriteListSet.has(id),
      title: displayShortName && fields.shortTitle ? fields.shortTitle : fields.title,
    }));
  },
);

export const {
  connect,
  getIds: getCatalogProductIdsList,
  getData,
} = composeContextSelectorGroup(CatalogProductsContext, getCatalogProductsList);

export const getCatalogProductsDict = getData;

/**
 * Selectors products delivery settings
 */
export const getCatalogOfficesRequestStatus = (state) =>
  getCatalogProductsData(state).updateOfficesRequestStatus || '';
export const getCatalogOfficesIsLoading = (state) =>
  getCatalogOfficesRequestStatus(state) === REQUEST_INPROGRESS;

const __delivery = createReduceSelector(getData, (data) => data || blankObj);

export const getCatalogProductPromotionId = createReduceSelector(
  __delivery,
  (data) => data.promotionId,
);
export const getCatalogProductTitle = createReduceSelector(__delivery, (data) => data.title);
export const getCatalogProductArticle = createReduceSelector(__delivery, (data) => data.article);

export const getCatalogProductOffices = createReduceSelector(__delivery, (data) => {
  const officesRaw = data.offices || [];
  return removeArrayDuplicates(officesRaw, (a, b) => a.pointCode === b.pointCode);
});

export const getCatalogProductOfficesLength = createReduceSelector(
  getCatalogProductOffices,
  (items) => items.length,
);

/**
 * Selector group, returning product data dictionaries, each selector returns dict by productId
 */
export const getProductIdsDict = createReduceSelector(getData, (item) => item.id);
export const getProductArticlesDict = createReduceSelector(getData, (item) => item.article);
export const getProductParameters = createReduceSelector(getData, (item) => item?.characteristics);
export const getProductMemory = createReduceSelector(
  getProductParameters,
  (params) => params?.find((param) => param?.label === 'Объем памяти')?.value,
);

/** stray fields */
export const getProductCharacteristicsDict = createReduceSelector(
  getData,
  (item) => item.characteristics || blankArr,
);
export const getProductInBasketDict = createReduceSelector(getData, (item) => item.isInBasket);
export const getProductTitleDict = createReduceSelector(getData, (item) => item.title);
export const getProductBrandNameDict = createReduceSelector(getData, (item) => item.brandName);
export const getProductUrlDict = createReduceSelector(getData, (item) => item.url);
export const getProductThumbUrlDict = createReduceSelector(getData, (item) => item.thumbUrl);
export const getProductSlugDict = createReduceSelector(getData, (item) => item.urlSlug || '');
export const getRealMainCollectionName = createReduceSelector(
  getData,
  (item) => item.mainCollectionName,
);

/** price */
export const getPriceDataDict = createReduceSelector(getData, (item) => item.price || blankObj);

export const getPriceDict = createReduceSelector(getPriceDataDict, (item) => item.value);
export const getOldPriceDict = createReduceSelector(getPriceDataDict, (item) => item.oldValue);
export const getDiscountPriceDict = createReduceSelector(
  getOldPriceDict,
  getPriceDict,
  (oldPrice, price) => Math.max(oldPrice - price, 0) || 0,
);

/** compare */
export const getIsProductComparedDict = createReduceSelector(
  getProductIdsDict,
  getComparedProductIds,
  (productId, comparedProducts) => comparedProducts.indexOf(productId) !== -1,
);

/** feedback */
const __feedback = createReduceSelector(getData, (item) => item.feedback);

export const getProductsFeedbackCountDict = createReduceSelector(__feedback, (item) => item.count);
export const getProductsFeedbackAvailabilityDict = createReduceSelector(
  getProductsFeedbackCountDict,
  (item) => !!item,
);
export const getProductsFeedbackRatingDict = createReduceSelector(
  __feedback,
  (item) => item.average,
);
export const getProductsFeedbackUrlDict = createReduceSelector(__feedback, (item) => item.url);

/** promotion bundle */
export const getBundleStateFlag = createReduceSelector(
  getData,
  (item) => item.promotionBundle.state,
);
export const getBundleOldPrice = createReduceSelector(
  getData,
  (item) => item.promotionBundle.oldPrice,
);
export const getBundlePrice = createReduceSelector(getData, (item) => item.promotionBundle.price);
export const getBundleMainDescription = createReduceSelector(
  getData,
  (item) => item.promotionBundle.mainDescription,
);
export const getBundleMainPrice = createReduceSelector(
  getData,
  (item) => item.promotionBundle.mainDescriptionPrice,
);
export const getBundleAdditionalDescription = createReduceSelector(
  getData,
  (item) => item.promotionBundle.additionalDescription,
);
export const getBundleAdditionalPrice = createReduceSelector(
  getData,
  (item) => item.promotionBundle.additionalDescriptionPrice,
);

export const getBundleDeliveryMethods = createReduceSelector(
  getData,
  (item) => item.promotionBundle.deliveryMethods || [],
);

/** Delivery widgets */
const __benefits = createReduceSelector(getData, (item) => item.delivery || {});
const getProductDeliveryMethods = createReduceSelector(__benefits, (data) => data.methods || []);
const useDeliveryMethods = createReduceSelector(__benefits, (data) => data.useMethods || false);
export const getDeliveryTabUrl = createReduceSelector(__benefits, (data) => data.pickupUrl || '');
export const getDeliveryCourierUrl = createReduceSelector(
  __benefits,
  (data) => data.courierUrl || '',
);
const getDeliveryMethodsList = createReduceSelector(
  getBundleStateFlag,
  getBundleDeliveryMethods,
  getProductDeliveryMethods,
  (isBundle, bundleMethods, productMethods) => (isBundle ? bundleMethods : productMethods),
);
const getDeliveryMethodsDict = createReduceSelector(
  getDeliveryMethodsList,
  convertListToObjectBy('type'),
);

/** old selectors for delivery widgets */

const getProductTakeawayAvailabilityDict = createReduceSelector(
  getData,
  (item) => item.hasRemainsOffice,
);
const getProductCourierAvailabilityDict = createReduceSelector(
  getData,
  (item) => item.hasRemainsShop,
);

export const getDeliveryBenefits = createReduceSelector(
  getBundleStateFlag,
  useDeliveryMethods,
  getDeliveryMethodsDict,
  getProductTakeawayAvailabilityDict,
  getProductCourierAvailabilityDict,
  (isBundle, useMethods, methods, hasTakeaway, hasCourier) => {
    let deliveryBenefits = [];
    if (useMethods) {
      /** for bundles and ordinary products */
      if ((methods || []).length === 0) return [];

      if (methods.pickup || methods.movements) {
        deliveryBenefits = [...deliveryBenefits, { slug: Product.PICKUP }];
      }

      if (methods.fast_delivery) {
        const deliveryTwoHours = {
          slug: Product.DELIVERY_TWO_HOURS,
          startTime: methods.fast_delivery.startTime,
          endTime: methods.fast_delivery.endTime,
        };

        if (methods.fast_delivery.delay) {
          deliveryBenefits = [...deliveryBenefits, deliveryTwoHours];
        }
      } else if (methods.courier) {
        const courierDelivery = {
          slug: methods.courier.price > 0 ? Product.DELIVERY : Product.FREE_DELIVERY,
          startTime: methods.courier.startTime,
          endTime: methods.courier.endTime,
        };

        deliveryBenefits = [...deliveryBenefits, courierDelivery];
      }
    } else {
      /** for not bundles only */
      deliveryBenefits =
        hasTakeaway && !isBundle ?
          [...deliveryBenefits, { slug: Product.PICKUP }]
        : deliveryBenefits;
      deliveryBenefits =
        hasCourier && !isBundle ?
          [...deliveryBenefits, { slug: Product.DELIVERY }]
        : deliveryBenefits;
    }
    return deliveryBenefits;
  },
);

export const getFastDeliveryDelay = createReduceSelector(getData, (item) => {
  const fastDeliveryMethods = item.delivery.methods.filter((cur) => cur.type === 'fast_delivery');
  return fastDeliveryMethods.length !== 0 ? fastDeliveryMethods[0].delay : null;
});

/** remains */
export const getProductRemainsDict = createReduceSelector(getData, (item) => item.remains);

/** leasing */
export const getProductLeasingAvailabity = createReduceSelector(getData, (item) => item.hasLeasing);

/** preorder */
const getCatalogPreorderData = (state) => getCatalogData(state).preorderDetails || blankObj;
const getCommonPreorderAnnouncementTemplate = (state) =>
  getCatalogPreorderData(state).announcementTemplate || '';
export const getProductPreorderAnnouncementTemplateDict = createReduceSelector(
  getData,
  (item) => item.preorderAnnouncementTemplate,
);

/**
 * product individual template is used when available
 * otherwise common template is used
 */
const getPreorderAnnouncementTemplateDict = createReduceSelector(
  getProductPreorderAnnouncementTemplateDict,
  getCommonPreorderAnnouncementTemplate,
  (individualTemplate, commonTemplate) => individualTemplate || commonTemplate,
);

export const getProductPreorderStartDateDict = createReduceSelector(getData, (item) =>
  convertISODateToString(item.preorderStartDate),
);
export const getProductIsPreorderDict = createReduceSelector(getData, (item) => item.isPreorder);
/** tariff flag selector */
export const getProductIsTariffDict = createReduceSelector(getData, (item) => item.isTariff);
export const getProductHasRemainsOffice = createReduceSelector(
  getData,
  (item) => item.hasRemainsOffice,
);
export const getProductHasRemainsShop = createReduceSelector(
  getData,
  (item) => item.hasRemainsShop,
);
export const getProductPreorderAnnouncementDict = createMarkupReduceSelector(
  getPreorderAnnouncementTemplateDict,
  {
    startDate: getProductPreorderStartDateDict,
  },
);
export const getProductOutOfStockEmojiPath = createReduceSelector(
  getCatalogProductsDict,
  (item) => item.emoji || '',
);
export const getProductOutOfStockClassName = createReduceSelector(
  getCatalogProductsDict,
  (item) => item.className || '',
);
/** tariff dpc id selector */
export const getProductDpcIdDict = createReduceSelector(getData, (item) => item.dpcId);

export const getProductIsOutOfStockDict = createReduceSelector(
  getData,
  getProductIsPreorderDict,
  (item, isPreorder) => !isPreorder && item.isOutOfStock,
);
export const getProductOutOfStockMessageTemplateDict = createReduceSelector(
  getData,
  (item) => item.outOfStockMessageTemplate,
);

export const getProductOutOfStockMessageDict = createAdjustableMarkupReduceSelector(
  getProductOutOfStockMessageTemplateDict,
);

/** content promotion */
export const getProductsContentPromotionDict = createReduceSelector(
  getData,
  (item) => item.contentPromotion || null,
);
export const getProductsContentPromotionUrlDict = createReduceSelector(
  getData,
  (item) => item.contentPromotionUrl || null,
);
export const getProductsHasActionCampaignsContentDict = createReduceSelector(
  getData,
  (item) => item.hasActionCampaigns,
);

/** badges */
export const getProductBadgesDict = createReduceSelector(getData, (item) => item.badges);
export const getProductBadgeListDict = createReduceSelector(getProductBadgesDict, (badges) =>
  badges.map((item) => item.title).join('|'),
);

/**
 * productsSEO
 */
export const getProductSeoDataDict = createReduceSelector(
  getProductIdsDict,
  getProductIsTariffDict,
  getProductTitleDict,
  getProductBrandNameDict,
  getPriceDict,
  getProductIsPreorderDict,
  getProductIsOutOfStockDict,
  getProductRemainsDict,
  getCurrentCategoriesPath,
  getCatalogSubCollectionId,
  (id, isTariff, name, brand, price, isPreorder, isOutOfStock, remains, categoriesList, subId) => ({
    id,
    name,
    brand,
    price,
    category: subId, // идентификатор категории
    list: categoriesList, // Раздел в формате "Телефоны|Смартфоны"
    // eslint-disable-next-line multiline-ternary
    ...(isTariff ? null : (
      {
        // dimension1: isPreorder && 2 || !isOutOfStock && 1 || 0,
        // 1 - Есть в наличии, 0 – нет в наличии, 2 - Предзаказ
        dimension16: remainsNumToText(remains),
      }
    )),
  }),
);

export const getProductSeoDataList = createSelector(
  getCatalogProductIdsList,
  getProductSeoDataDict,
  (ids, seoData) => ids.map((id) => seoData[id]),
);

export const getProductFullSeoDataDict = createReduceSelector(
  getProductSeoDataDict,
  getProductsContentPromotionDict,
  getDiscountPriceDict,
  getProductsFeedbackRatingDict,
  getProductBadgeListDict,
  getCatalogProductIdsList,
  getRealMainCollectionName,
  getCatalogSubCollectionName,
  getProductArticlesDict,
  getProductMemory,
  (
    seoData,
    contentPromotion,
    discount,
    rating,
    badgeList,
    ids,
    collectionName,
    subCollectionName,
    article,
    memory,
  ) => ({
    ...seoData,
    position: ids.indexOf(seoData.id) + 1 || null, // starting with 1, if missing null is returned
    dimension17: contentPromotion,
    metric14: discount,
    metric15: rating || 0,
    dimension15: badgeList,
    collectionName,
    subCollectionName,
    article,
    variant: `-_${memory}`,
  }),
);

/* esim */

export const getProductHasESim = createReduceSelector(
  getData,
  (item) => !!(item.esim || {}).hasESim,
);
export const getProductHasNfc = createReduceSelector(getData, (item) => !!item?.NFC?.hasNFC);
export const getProductPartnerSlug = createReduceSelector(getData, (item) => item.partnerSlug);
export const getProductIsPartner = createReduceSelector(getData, (item) => !!item.partnerSlug);

export const getCatalogIsInBasket = createSelector(
  getCatalogProductsListRaw,
  getBasketProductIds,
  (list, addedItems) =>
    list.map(({ id, ...otherFields }) => ({
      ...otherFields,
      id,
      isInBasket: addedItems.indexOf(id) !== -1,
    })),
);

export const getProductInBasket = createReduceSelector(
  getData,
  getBasketProductIds,
  (item, ids) => ids.indexOf(`${item.id}`) !== -1,
);

/** return *Object* with productIds in keys and hasBnpl in values  */
export const selectIsBnplAvailableInProduct = createReduceSelector(getData, (item) => {
  const hasBnpl = item.installments.find((installment) => installment.type === 'bnpl');
  return !!hasBnpl;
});

export const selectIsInstallmentAvailableInProduct = createReduceSelector(getData, (item) => {
  const hasInstallment = item.installments.find(
    (installment) => installment.type === 'installment',
  );
  return !!hasInstallment;
});
